import React, { useState } from "react";
import { ShieldCheck, Loader2, AlertCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { signPdfWithAutoFirma } from "@/lib/autofirma";
import {
    getTenderAnnexSignPayload,
    uploadSignedTenderAnnex,
    downloadSignedTenderAnnexUrl,
    api,
} from "@/lib/api";

/**
 * SignTenderAnnexButton — triggers AutoFirma to sign a single tender annex
 * (PAdES) and uploads the signed PDF back to the server. Designed to be used
 * inside the Annexes tab of a tender.
 *
 * Props:
 *   tenderId — id of the tender owning the annex
 *   annex – Annex object (must include id, title, signed_storage_path?)
 *   onSigned() – callback fired after successful signature (parent should
 *                re-fetch the tender to refresh its state)
 */
export function SignTenderAnnexButton({ tenderId, annex, onSigned }) {
    const [signing, setSigning] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const isSigned = !!annex?.signed_storage_path;

    const onClick = async () => {
        setSigning(true);
        try {
            const payload = await getTenderAnnexSignPayload(tenderId, annex.id);
            const result = await signPdfWithAutoFirma(payload.pdf_base64);
            await uploadSignedTenderAnnex(
                tenderId,
                annex.id,
                result.signedB64,
                result.dn
            );
            toast.success(`«${annex.title}» firmado`);
            onSigned?.();
        } catch (err) {
            const msg = err?.message || "No se pudo firmar el anexo";
            if (
                /no está instalado|AutoScript no se cargó|no se pudo descargar/i.test(
                    msg
                )
            ) {
                setHelpOpen(true);
            } else if (/cancelada por el usuario/i.test(msg)) {
                toast.info("Firma cancelada");
            } else {
                toast.error(msg);
            }
        } finally {
            setSigning(false);
        }
    };

    const onDownloadSigned = async () => {
        try {
            const res = await api.get(
                `/tenders/${tenderId}/annexes/${annex.id}/download/signed-pdf`,
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `${(annex.title || "anexo").slice(0, 60)}.firmado.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("No se pudo descargar el PDF firmado");
        }
    };

    if (isSigned) {
        return (
            <>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={onDownloadSigned}
                    className="rounded-xl border-emerald-300 bg-emerald-50 text-emerald-700 font-bold text-xs gap-1.5 hover:bg-emerald-100"
                    title={annex.signer_dn ? `Firmado por: ${annex.signer_dn}` : "Anexo firmado"}
                    data-testid={`tender-annex-signed-${annex.id}`}
                >
                    <ShieldCheck size={13} /> Firmado · PAdES
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={onClick}
                    disabled={signing}
                    className="rounded-xl h-8 w-8 p-0 text-ink-400 hover:text-brand-600"
                    title="Volver a firmar"
                    data-testid={`tender-annex-resign-${annex.id}`}
                >
                    {signing ? (
                        <Loader2 size={13} className="animate-spin" />
                    ) : (
                        <ShieldCheck size={13} />
                    )}
                </Button>
            </>
        );
    }

    return (
        <>
            <Button
                type="button"
                size="sm"
                onClick={onClick}
                disabled={signing}
                className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-xs gap-1.5 shadow-pop disabled:opacity-60"
                title="Firmar con AutoFirma (certificado digital local)"
                data-testid={`tender-annex-sign-${annex.id}`}
            >
                {signing ? (
                    <>
                        <Loader2 size={13} className="animate-spin" />
                        Firmando…
                    </>
                ) : (
                    <>
                        <ShieldCheck size={13} />
                        Firmar
                    </>
                )}
            </Button>

            <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle size={18} className="text-amber-600" />
                            AutoFirma no detectada
                        </DialogTitle>
                        <DialogDescription className="pt-2 space-y-3 text-sm">
                            <p>
                                <strong>AutoFirma</strong> es la aplicación oficial gratuita
                                del Gobierno de España para firmar documentos PDF con tu
                                certificado digital (FNMT, DNIe, Cl@ve PIN, etc.).
                            </p>
                            <a
                                href="https://firmaelectronica.gob.es/Home/Descargas.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-brand-600 font-bold hover:underline"
                                data-testid="autofirma-install-link-tender"
                            >
                                Descargar AutoFirma
                                <ExternalLink size={12} />
                            </a>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            onClick={() => setHelpOpen(false)}
                            className="rounded-xl"
                            data-testid="autofirma-help-close-tender"
                        >
                            Entendido
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
